import { extend, ResponseError } from 'umi-request';
import { message as antdMessage, notification } from 'antd';
import { history, getLocale } from '@umijs/max';
import { v4 as uuidv4 } from 'uuid';

// umiJS max不支持请求失败后retry
// 只能刷新加载画面，用户体验极差
// 如果要实现token过期自动retry,只能使用自定义requestInstance
// 最大5S,如果后台没有返回，中止，报错，  alert (msg) 。

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean; // 后端返回的 success 字段
  code: string; // 统一用字符串表示错误码，跟后端对应
  message: string; // 错误提示或者成功消息
  data?: any;
  showType?: ErrorShowType; // 可选，决定如何展示错误
}

// 设备uuid
const DEVICE_ID_KEY = 'DEVICE_ID';
const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};
const loginPath = '/user/login';

let isRefreshing = false; // 标记是否正在刷新 token
let queue: Array<() => void> = []; // 刷新期间挂起的请求回调队列

// 创建umiJS实例
const request = extend({
  credentials: 'include',
  headers: {},
  timeout: 10000,
  errorHandler: async (error: ResponseError & { info?: any }) => {
    if (error.request?.options?.skipErrorHandler) throw error;

    // BizError 是我们自定义抛出的
    if (error.name === 'BizError') {
      const errorInfo = error.info;
      if (errorInfo) {
        const { message: msg, code, showType } = errorInfo;
        const formattedMessage = <div style={{ whiteSpace: 'pre-line' }}>{msg}</div>;

        switch (showType) {
          case 0: // SILENT
            break;
          case 1: // WARN_MESSAGE
            antdMessage.warning(formattedMessage);
            break;
          case 2: // ERROR_MESSAGE
            antdMessage.error(formattedMessage);
            break;
          case 4: // NOTIFICATION
            notification.open({
              description: formattedMessage,
              message: code,
            });
            break;
          case 9: // REDIRECT
            break;
          default:
            antdMessage.error(formattedMessage);
        }
      }
    } else if (error.response) {
      // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
      const status = error.response.status;
      const res = (await error.response.clone().json()) as ResponseStructure;
      const errorMsg = `Response status: ${status}`;
      const msg = res.message;
      antdMessage.error(msg);
      console.error(errorMsg);
      // 抛出错误，让调用方知道请求失败
      throw error;
    } else if (error.request) {
      // 请求已经成功发起，但没有收到响应
      // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
      // 而在node.js中是 http.ClientRequest 的实例
      // antdMessage.error('None response! Please retry.');
      antdMessage.error('通信エラーが発生しました。管理者に連絡してください。', 3);
    } else {
      // 发送请求时出了点问题
      antdMessage.error('通信エラーが発生しました。管理者に連絡してください。', 3);
    }
  },
});

// 请求拦截器：加 deviceId 和 Accept-Language
request.interceptors.request.use((url, options) => {
  const deviceId = getDeviceId();
  const currentLocale = getLocale() || 'ja-JP';
  // 将 UmiJS locale 格式转换为 Accept-Language 格式
  // zh-CN -> zh-CN, ja-JP -> ja-JP, en-US -> en-US
  const acceptLanguage = currentLocale;
  return {
    url,
    options: {
      ...options,
      headers: {
        ...options.headers,
        'X-Device-Id': deviceId,
        'Accept-Language': acceptLanguage,
      },
    },
  };
});

// 响应拦截器：处理 token 过期自动刷新，防止多次刷新
request.interceptors.response.use(async (response, options) => {
  // 获取API返回值
  const res = await response.clone().json();
  // 如果后台返回 token 过期
  if (res?.code === 'E0016') {
    const originalRequest = options;
    // 如果当前不在刷新中，则开始刷新流程
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const refreshResponse = await request<any>('/api/refresh-token', {
          method: 'POST',
          skipErrorHandler: true,
        });
        const isSuccess =
          refreshResponse && (refreshResponse.success || refreshResponse.code === 'SUCCESS');
        if (isSuccess) {
          // 刷新成功，执行队列中的请求
          queue.forEach((cb) => cb());
          queue = [];
          // 重试当前请求
          return request(originalRequest.url, originalRequest);
        } else {
          // 刷新失败，清除状态并提示
          history.push(loginPath);
        }
      } catch (e) {
        history.push(loginPath);
      } finally {
        isRefreshing = false;
        queue = [];
      }
    } else {
      // 正在刷新，等待刷新结果
      return new Promise((resolve, reject) => {
        queue.push(() => {
          request(originalRequest.url, originalRequest).then(resolve).catch(reject);
        });
      });
    }
  }

  // 如果后台返回token形式错误解析失败或者token已经无效
  if (res?.code === 'E0015' || res?.code === 'E0017') {
    // 刷新失败，清除状态并提示
    history.push(loginPath);
  }

  return response;
});

export default request;
